/**
 * web-account-pages D2 "Filters": the messages panel's filter row (Gabriel 2026-10-22). One table
 * says, for each filter, which rooms it reads, the server query of each read, and which entries
 * it keeps, by room AND kind of entry:
 * - All: both rooms, Telegram unfiltered. Secure: the Secure room only. Telegram: the Telegram
 *   room only, unfiltered.
 * - Chat: every typed message from the client and the studio in both rooms: the Secure room's
 *   messages and the Telegram room under the server's `chat` lens (the client's messages and
 *   media, the studio's replies).
 * - Bookings, Forms, Payments: the Telegram room's notices under the server lens of the same name,
 *   as the Mini App's History lenses read them. Their refresh delta and catch-up are unfiltered
 *   server-side, so the table keeps notices only.
 * The Secure room is never sent a server `filter` (the route refuses it). The keeps are a record by
 * entry kind, so a new kind (the Secure room's coming attachments: a form, booking or invite) cannot
 * be added without placing it under every filter, Secure and its own kind's among them.
 */
import type { ClientThreadEntry, ClientThreadEntryKind, ClientThreadFilter, ThreadRoom } from '@telify/shared';

export const WEB_MESSAGE_FILTERS = ['all', 'secure', 'telegram', 'chat', 'bookings', 'forms', 'payments'] as const;
export type WebMessageFilter = (typeof WEB_MESSAGE_FILTERS)[number];

/** The `GET /chat` query of one room's read: the Secure room never carries a `filter`. */
export type WebRoomQuery = { room: 'secure' } | { room: 'telegram'; filter: ClientThreadFilter };

type Keeps = Readonly<Record<ClientThreadEntryKind, boolean>>;

/** One room a filter reads. */
export interface WebFilterRead {
  readonly room: ThreadRoom;
  readonly query: WebRoomQuery;
  readonly keeps: Keeps;
}

const EVERYTHING: Keeps = { 'chat-message': true, 'chat-media': true, reply: true, automatic: true };
const TYPED: Keeps = { 'chat-message': true, 'chat-media': true, reply: true, automatic: false };
const NOTICES: Keeps = { 'chat-message': false, 'chat-media': false, reply: false, automatic: true };

const secureRead = (keeps: Keeps): WebFilterRead => ({ room: 'secure', query: { room: 'secure' }, keeps });
const telegramRead = (filter: ClientThreadFilter, keeps: Keeps): WebFilterRead => ({ room: 'telegram', query: { room: 'telegram', filter }, keeps });

const FILTER_TABLE: Readonly<Record<WebMessageFilter, readonly WebFilterRead[]>> = {
  all: [secureRead(EVERYTHING), telegramRead('all', EVERYTHING)],
  secure: [secureRead(EVERYTHING)],
  telegram: [telegramRead('all', EVERYTHING)],
  chat: [secureRead(TYPED), telegramRead('chat', TYPED)],
  bookings: [telegramRead('bookings', NOTICES)],
  forms: [telegramRead('forms', NOTICES)],
  payments: [telegramRead('payments', NOTICES)],
};

/** The rooms a filter reads, each with its server query and the entries it keeps. */
export function webFilterReads(filter: WebMessageFilter): readonly WebFilterRead[] {
  return FILTER_TABLE[filter];
}

/** Whether the studio's typing line shows under the filter (row M12): only where its secure messages show. */
export function webFilterShowsTyping(filter: WebMessageFilter): boolean {
  return FILTER_TABLE[filter].some((read) => read.room === 'secure');
}

/** Whether the filter keeps an entry of a room: false for a room it does not read. */
export function webFilterKeeps(filter: WebMessageFilter): (room: ThreadRoom, entry: ClientThreadEntry) => boolean {
  const reads = FILTER_TABLE[filter];
  return (room, entry) => reads.some((read) => read.room === room && read.keeps[entry.kind]);
}
